import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import NotificationBell from './NotificationBell';
import SyncIndicator from './SyncIndicator';
import LanguageSwitcher from './LanguageSwitcher';
import ThemeToggle from './ThemeToggle';
import BiometricGate from './BiometricGate';
import { 
  LayoutDashboard, 
  Building2, 
  Users, 
  FolderOpen, 
  Upload, 
  BarChart3, 
  Box, 
  Ruler, 
  FileText, 
  Bot, 
  LogOut, 
  Menu, 
  X,
  UserCircle,
  Settings,
  ClipboardCheck,
  Webhook,
  ShieldCheck,
  Layers,
  Plug
} from 'lucide-react';

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const getSidebarItems = () => {
    if (!user) return [];

    if (user.role === 'SuperAdmin') {
      return [
        { path: '/superadmin/dashboard', icon: LayoutDashboard, label: t('nav.dashboard'), testId: 'dashboard' },
        { path: '/superadmin/companies', icon: Building2, label: t('nav.companies'), testId: 'companies' },
        { path: '/superadmin/users', icon: Users, label: 'Users', testId: 'sa-users' },
        { path: '/superadmin/settings', icon: Settings, label: t('nav.settings'), testId: 'settings' },
      ];
    }

    if (user.role === 'CompanyAdmin') {
      return [
        { path: '/admin/dashboard', icon: LayoutDashboard, label: t('nav.dashboard'), testId: 'dashboard' },
        { path: '/admin/analytics', icon: BarChart3, label: t('nav.analytics'), testId: 'analytics' },
        { path: '/admin/integrations', icon: Webhook, label: t('nav.apiWebhooks'), testId: 'api-&-webhooks' },
        { path: '/admin/audit-logs', icon: ShieldCheck, label: t('nav.auditLog'), testId: 'audit-log' },
        { path: '/admin/privacy', icon: ShieldCheck, label: 'Privacy & Compliance', testId: 'privacy-center' },
        { path: '/admin/settings', icon: Settings, label: t('nav.settings'), testId: 'settings' },
        { path: '/admin/profile', icon: UserCircle, label: t('nav.profile'), testId: 'profile' },
        { path: '/admin/users', icon: Users, label: t('nav.users'), testId: 'users' },
        { path: '/admin/projects', icon: FolderOpen, label: t('nav.projects'), testId: 'projects' },
        { path: '/admin/portfolios', icon: Layers, label: 'Portfolios', testId: 'portfolios' },
        { path: '/admin/integration-hub', icon: Plug, label: 'Integration Hub', testId: 'integration-hub' },
      ];
    }

    return [
      { path: '/app/dashboard', icon: LayoutDashboard, label: t('nav.dashboard'), testId: 'dashboard' },
      { path: '/app/projects', icon: FolderOpen, label: t('nav.projects'), testId: 'projects' },
      { path: '/app/my-issues', icon: ClipboardCheck, label: t('nav.myIssues'), testId: 'my-issues' },
      { path: '/app/privacy', icon: ShieldCheck, label: 'Privacy & My Data', testId: 'privacy-my-data' },
      { path: '/app/assistant', icon: Bot, label: t('nav.aiAssistant'), testId: 'ai-assistant' },
    ];
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  const sidebarItems = getSidebarItems();

  // V28 mobile bottom navigation — field-first, role-aware, curated from existing destinations
  // (no duplicate routes). The 5th tab "More" opens the full drawer, so nothing is unreachable.
  const getBottomNavItems = () => {
    if (!user) return [];
    if (user.role === 'SuperAdmin') {
      return [
        { path: '/superadmin/dashboard', icon: LayoutDashboard, label: t('nav.dashboard'), testId: 'dashboard' },
        { path: '/superadmin/companies', icon: Building2, label: t('nav.companies'), testId: 'companies' },
        { path: '/superadmin/users', icon: Users, label: 'Users', testId: 'sa-users' },
        { path: '/superadmin/settings', icon: Settings, label: t('nav.settings'), testId: 'settings' },
      ];
    }
    if (user.role === 'CompanyAdmin') {
      return [
        { path: '/admin/dashboard', icon: LayoutDashboard, label: t('nav.dashboard'), testId: 'dashboard' },
        { path: '/admin/projects', icon: FolderOpen, label: t('nav.projects'), testId: 'projects' },
        { path: '/admin/analytics', icon: BarChart3, label: t('nav.analytics'), testId: 'analytics' },
        { path: '/admin/users', icon: Users, label: t('nav.users'), testId: 'users' },
      ];
    }
    return [
      { path: '/app/dashboard', icon: LayoutDashboard, label: t('nav.dashboard'), testId: 'dashboard' },
      { path: '/app/projects', icon: FolderOpen, label: t('nav.projects'), testId: 'projects' },
      { path: '/app/my-issues', icon: ClipboardCheck, label: t('nav.myIssues'), testId: 'my-issues' },
      { path: '/app/assistant', icon: Bot, label: t('nav.aiAssistant'), testId: 'ai-assistant' },
    ];
  };
  const bottomNavItems = getBottomNavItems();

  return (
    <div className="rvx-app-shell h-screen flex flex-col overflow-hidden bg-gradient-to-br from-slate-50 via-slate-100 to-slate-200 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
      <BiometricGate />
      {/* Header — fixed at top, above all other layout components */}
      <header className="rvx-safe-top shrink-0 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 z-50 shadow-sm">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="md:hidden p-2 -ml-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
              data-testid="mobile-menu-toggle"
              aria-label="Menu"
            >
              {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
            <div className="flex items-center gap-3">
              <img src={require('../assets/branding/reasonvisionx-mark.png')} alt="ReasonVisionX" className="w-9 h-9 object-contain" data-testid="header-brand-mark" />
              <div>
                <h1 className="text-base sm:text-xl font-bold text-slate-900 dark:text-white font-display leading-tight">ReasonVisionX</h1>
                <p className="hidden sm:block text-xs text-slate-500 dark:text-slate-400">{t('header.tagline')}</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-4">
            <ThemeToggle />
            <LanguageSwitcher authed={!!user} />
            <SyncIndicator />
            {user && user.role !== 'SuperAdmin' && <NotificationBell />}
            <div className="text-right hidden sm:block">
              <p className="text-sm font-medium text-slate-900 dark:text-slate-100" data-testid="user-name">{user?.name}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400">{user?.role}</p>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-3 sm:px-4 py-2 min-h-[44px] bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 rounded-lg transition-colors"
              data-testid="logout-button"
              aria-label={t('header.logout')}
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">{t('header.logout')}</span>
            </button>
          </div>
        </div>
      </header>

      {/* Body row — sidebar + main, each with independent scroll */}
      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* Sidebar — sits below the header; scrolls independently of main content */}
        <aside
          className={`
            fixed md:static top-[57px] md:top-0 left-0 z-40
            h-[calc(100vh-57px)] md:h-auto w-64 shrink-0
            bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800 shadow-lg md:shadow-none
            overflow-y-auto
            transition-transform duration-300 ease-in-out
            ${sidebarOpen ? 'translate-x-0 visible' : '-translate-x-full invisible md:visible md:translate-x-0'}
          `}
          data-testid="sidebar"
        >
          <nav className="p-4 space-y-1">
            {sidebarItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              return (
                <button
                  key={item.path}
                  onClick={() => {
                    navigate(item.path);
                    setSidebarOpen(false);
                  }}
                  className={`
                    w-full flex items-center gap-3 px-4 py-3 min-h-[44px] rounded-lg transition-all
                    ${active 
                      ? 'bg-blue-50 dark:bg-blue-950/60 text-blue-700 dark:text-blue-300 font-medium shadow-sm' 
                      : 'text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white'
                    }
                  `}
                  data-testid={`nav-${item.testId}`}
                >
                  <Icon className="w-5 h-5 shrink-0" />
                  <span>{item.label}</span>
                </button>
              );
            })}
            {/* Compliance links — reachable on phones via the drawer (footer is desktop-only) */}
            <div className="md:hidden pt-4 mt-4 border-t border-slate-200 dark:border-slate-800 text-xs text-slate-500 dark:text-slate-400 rvx-safe-bottom" data-testid="drawer-legal-links">
              <a href="/privacy-policy.html" target="_blank" rel="noopener noreferrer" className="block px-4 py-2 hover:underline">Privacy Policy</a>
              <a href="/data-deletion.html" target="_blank" rel="noopener noreferrer" className="block px-4 py-2 hover:underline">Data Deletion</a>
              <p className="px-4 pt-2 leading-relaxed">© {new Date().getFullYear()} Reason Analytics Softtech Pvt Limited · v1.0.0</p>
            </div>
          </nav>
        </aside>

        {/* Mobile Sidebar Overlay */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 top-[57px] bg-black bg-opacity-50 z-30 md:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main Content — scrolls independently of the sidebar */}
        <main className="rvx-app-main flex-1 min-w-0 overflow-y-auto" data-testid="main-content">
          <div className="p-4 sm:p-6 md:p-8 pb-24 md:pb-8 max-w-7xl mx-auto w-full">
            {children}
          </div>
        </main>
      </div>

      {/* Mobile bottom navigation — phones only; desktop keeps the sidebar rail unchanged */}
      <nav
        className="md:hidden rvx-safe-bottom shrink-0 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 z-40 flex"
        data-testid="mobile-bottom-nav"
      >
        {bottomNavItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.path);
          return (
            <button
              key={item.path}
              onClick={() => { navigate(item.path); setSidebarOpen(false); }}
              className={`flex-1 flex flex-col items-center justify-center gap-0.5 py-2 min-h-[56px] transition-colors ${
                active ? 'text-blue-700 dark:text-blue-300' : 'text-slate-500 dark:text-slate-400'
              }`}
              data-testid={`bottomnav-${item.testId}`}
              aria-label={item.label}
            >
              <Icon className="w-5 h-5" />
              <span className="text-[10px] font-medium leading-none truncate max-w-full px-1">{item.label}</span>
            </button>
          );
        })}
        <button
          onClick={() => setSidebarOpen(true)}
          className="flex-1 flex flex-col items-center justify-center gap-0.5 py-2 min-h-[56px] text-slate-500 dark:text-slate-400 transition-colors"
          data-testid="bottomnav-more"
          aria-label="More"
        >
          <Menu className="w-5 h-5" />
          <span className="text-[10px] font-medium leading-none">More</span>
        </button>
      </nav>

      {/* Footer — desktop/tablet only; phones use the bottom nav + drawer legal links */}
      <footer className="hidden md:block shrink-0 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 py-4 px-6 text-center text-sm text-slate-600 dark:text-slate-400 z-40">
        <div>ReasonVisionX — Visual intelligence for real-world construction | © {new Date().getFullYear()} Reason Analytics Softtech Pvt Limited | Version v1.0.0</div>
        <div className="mt-1 text-xs" data-testid="footer-legal-links">
          <a href="/privacy-policy.html" target="_blank" rel="noopener noreferrer" className="hover:underline" data-testid="footer-privacy-policy-link">Privacy Policy</a>
          <span className="mx-2">·</span>
          <a href="/data-deletion.html" target="_blank" rel="noopener noreferrer" className="hover:underline" data-testid="footer-data-deletion-link">Data Deletion</a>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
